import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useConsent } from "@/lib/ConsentContext";
import {
  applyGoogleConsent,
  applyStoredConsent,
  getStoredConsent,
  isAdUserDataGranted,
  CONSENT_STORAGE_KEY,
} from "@/lib/googleConsent";

let INSTALLED = { ga: false, ads: false, meta: false, tiktok: false, snap: false };

/** Read-only view of which destinations THIS loader has installed. Exposed
 * so purchase-conversion logic can wait for the Google Ads destination to
 * be registered before dispatching `gtag('event', 'conversion', ...)`. */
export function isAdsInstalled() {
  return INSTALLED.ads === true;
}
export function getInstalled() {
  return { ...INSTALLED };
}

function inject(src) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.async = true; s.src = src; document.head.appendChild(s);
}
function run(code) { const s = document.createElement("script"); s.text = code; document.head.appendChild(s); }

/**
 * Google gtag.js loader. Under Google Consent Mode v2 the gtag.js
 * script MUST load unconditionally — the `default denied` block in
 * index.html is what actually suppresses cookies + user data until a
 * consent update lands. This function installs BOTH the GA4 and
 * Google Ads destinations in a single gtag.js script tag whenever
 * either ID is configured.
 *
 * Idempotent: safe to call on every effect run.
 */
function installGoogleTag(t) {
  // At least one Google ID must be configured — otherwise there is no
  // gtag.js script URL to load and nothing to register.
  const primaryId = t.ga4_id || t.google_ads_id;
  if (!primaryId) return;

  if (!INSTALLED.ga && !INSTALLED.ads) {
    inject(`https://www.googletagmanager.com/gtag/js?id=${primaryId}`);
    // The gtag stub was defined in index.html before this script parses;
    // this call just ensures `window.gtag` is exposed for the rest of
    // the app (and idempotent-safe if index.html was somehow skipped).
    run(
      `window.dataLayer=window.dataLayer||[];` +
      `function gtag(){dataLayer.push(arguments);}window.gtag=gtag;` +
      `gtag('js', new Date());`
    );
  }
  if (t.ga4_id && !INSTALLED.ga) {
    run(`gtag('config','${t.ga4_id}',{anonymize_ip:true});`);
    INSTALLED.ga = true;
  }
  if (t.google_ads_id && !INSTALLED.ads) {
    run(`gtag('config','${t.google_ads_id}');`);
    INSTALLED.ads = true;
  }
}

/**
 * Non-Google advertising pixels (Meta / TikTok / Snapchat). These
 * vendors do NOT participate in Google Consent Mode, so we keep them
 * gated on the DJ SPY `advertising` category as before. Untouched by
 * the Consent Mode v2 wiring per the task spec.
 */
function installNonGoogleAds(t) {
  if (t.meta_pixel_id && !INSTALLED.meta) {
    run(`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${t.meta_pixel_id}');fbq('track','PageView');`);
    INSTALLED.meta = true;
  }
  if (t.tiktok_pixel_id && !INSTALLED.tiktok) {
    run(`!function(w,d,tt){w.TiktokAnalyticsObject=tt;var ttq=w[tt]=w[tt]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+tt;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${t.tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`);
    INSTALLED.tiktok = true;
  }
  if (t.snap_pixel_id && !INSTALLED.snap) {
    run(`(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script',r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');snaptr('init','${t.snap_pixel_id}');snaptr('track','PAGE_VIEW');`);
    INSTALLED.snap = true;
  }
}

const VERIF_ATTR = "data-djspy-verif";

function upsertMeta(name, content) {
  const trimmed = String(content || "").trim();
  const existing = document.head.querySelector(`meta[name="${name}"]`);

  if (existing) {
    // A meta tag with this name is already in the HTML. If we own it (marked
    // with our attribute), we can update or remove it. If it came from the
    // server-rendered HTML (no marker), we ONLY overwrite when the admin
    // provided a non-empty override — never blank it out.
    const ownsIt = existing.getAttribute(VERIF_ATTR) === "1";
    if (trimmed) {
      existing.setAttribute("content", trimmed);
      if (!ownsIt) return; // leave the server-side tag as-is (now with new value)
    } else if (ownsIt) {
      existing.remove();
    }
    return;
  }
  if (!trimmed) return; // nothing to inject
  const el = document.createElement("meta");
  el.setAttribute("name", name);
  el.setAttribute("content", trimmed);
  el.setAttribute(VERIF_ATTR, "1");
  document.head.appendChild(el);
}

function pruneStaleVerifications(currentByMetaName) {
  // Only remove tags WE injected that no longer have a matching non-empty
  // config value. Server-rendered tags (no marker) are never removed here.
  const injected = document.head.querySelectorAll(`meta[${VERIF_ATTR}]`);
  injected.forEach((el) => {
    const name = el.getAttribute("name") || "";
    if (!currentByMetaName[name]) el.remove();
  });
}

function installVerifications(verif) {
  if (!verif || typeof verif !== "object") return;
  const byMetaName = {};
  Object.entries(verif).forEach(([k, v]) => {
    // Backend snake_case maps directly to the standard meta name for each
    // provider (google-site-verification, bing-site-verification, etc.).
    // Any new provider added on the backend flows through automatically.
    const metaName = k.replace(/_/g, "-");
    byMetaName[metaName] = String(v || "").trim();
    upsertMeta(metaName, byMetaName[metaName]);
  });
  pruneStaleVerifications(byMetaName);
}

export default function TrackingLoader() {
  const consent = useConsent();
  const [tracking, setTracking] = useState(null);

  // 1) Fetch tracking IDs from site-config. Verifications remain outside
  //    the consent gate (they set no cookies, transmit no user data).
  useEffect(() => {
    api.get("/site-config").then((r) => {
      setTracking(r.data?.tracking || {});
      installVerifications(r.data?.verifications || {});
    }).catch(() => {});
  }, []);

  // 2) Restore Google Consent Mode state for returning visitors BEFORE
  //    the `wait_for_update: 500` window in index.html closes. This
  //    dispatches `gtag('consent', 'update', ...)` so gtag.js — which
  //    will parse in step 3 below — sees the correct granted/denied
  //    states from its very first ping. If no stored consent exists
  //    (fresh visitor), the default-denied block in index.html stays
  //    in effect until the CMP writes an explicit choice.
  //
  //    We also listen for consent changes: cross-tab via the native
  //    `storage` event and same-tab via the custom `djspy-consent`
  //    event dispatched by ConsentContext.save().
  useEffect(() => {
    applyStoredConsent();

    const onStorageEvent = (e) => {
      // Native `storage` event fires only for OTHER tabs. e.key === null
      // signals a full clear; treat that as a reset (all denied).
      if (e && e.key !== null && e.key !== CONSENT_STORAGE_KEY) return;
      applyGoogleConsent(getStoredConsent());
    };
    const onSameTabConsent = () => {
      applyGoogleConsent(getStoredConsent());
    };
    window.addEventListener("storage", onStorageEvent);
    window.addEventListener("djspy-consent", onSameTabConsent);
    return () => {
      window.removeEventListener("storage", onStorageEvent);
      window.removeEventListener("djspy-consent", onSameTabConsent);
    };
  }, []);

  // 3) Install the Google tag unconditionally (Consent Mode v2 handles
  //    the suppression) and only install non-Google advertising pixels
  //    when the DJ SPY `advertising` category is explicitly granted.
  //    We also re-apply the current consent state right after loading
  //    gtag.js so its first `consent` event carries the correct values
  //    even if the initial `applyStoredConsent` call above happened
  //    before `window.gtag` existed.
  useEffect(() => {
    if (!tracking) return;
    installGoogleTag(tracking);
    applyGoogleConsent(consent?.prefs || getStoredConsent());
    if (consent?.prefs?.advertising) installNonGoogleAds(tracking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking, consent?.prefs?.analytics, consent?.prefs?.advertising]);

  return null;
}

/** Google Ads Purchase Conversion — configured on djspyofficial.com.
 * The base gtag('config', 'AW-341295704') tag is installed globally on
 * production; we simply fire the conversion event once per verified
 * successful Stripe payment. On preview / cookie-consent-off, `window.gtag`
 * won't be present and this is a no-op that returns `false` so the caller
 * can retry once the tag has loaded.
 */
const GOOGLE_ADS_CONVERSION_ID = "AW-341295704";
const GOOGLE_ADS_PURCHASE_LABEL = "LVHsCN_k1c4cENiE36IB";
const GOOGLE_ADS_SEND_TO = `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`;

const GOOGLE_ADS_FIRED_KEY = (orderId) => `djspy_google_ads_fired_${orderId}`;
const OTHER_PIXELS_FIRED_KEY = (orderId) => `djspy_pixels_fired_${orderId}`;

function _devLog(...args) {
  // Temporary debug logs — safe in production (no PII, tiny volume).
  try { console.log("[gads]", ...args); } catch { /* noop */ }
}

/** Fire the Google Ads Purchase Conversion exactly once per order.
 *
 * Returns `true` iff `gtag('event', 'conversion', ...)` executed successfully
 * on THIS call (or was previously executed successfully and the dedupe key
 * is present). Returns `false` when:
 *   - `orderId` / `value` are missing;
 *   - `window.gtag` is unavailable (script not yet loaded, or consent denied);
 *   - the gtag call throws.
 *
 * The dedupe key `djspy_google_ads_fired_<orderId>` is written ONLY after
 * a confirmed-successful call. Never poisoned by a failed attempt, so the
 * caller (Checkout.jsx) can retry until `window.gtag` is available.
 */
/** Enhanced Conversions payload builder. Google's gtag automatically
 *  SHA-256 hashes these fields client-side before dispatching them, but
 *  they still MUST be normalised (trim, lower-case email, strip spaces
 *  from phone) or the match rate collapses. Any field that ends up empty
 *  after normalisation is dropped so we never send `email: ""` etc.
 *  Nothing here is ever logged — the return value is passed straight to
 *  `gtag('set', 'user_data', ...)`. */
function _buildUserData({ email, phone, firstName, lastName, country } = {}) {
  const out = {};
  const e = (email || "").trim().toLowerCase();
  if (e && /.+@.+\..+/.test(e)) out.email = e;
  // Phone: strip everything except digits + a single leading `+`.
  const rawPhone = (phone || "").trim();
  if (rawPhone) {
    const digits = rawPhone.replace(/[^\d]/g, "");
    if (digits) out.phone_number = rawPhone.startsWith("+") ? `+${digits}` : digits;
  }
  const addr = {};
  if (firstName && firstName.trim()) addr.first_name = firstName.trim().toLowerCase();
  if (lastName && lastName.trim()) addr.last_name = lastName.trim().toLowerCase();
  if (country && country.trim()) addr.country = country.trim().toUpperCase();
  if (Object.keys(addr).length) out.address = addr;
  return out;
}

function fireGoogleAdsConversion({ value, currency, orderId, paymentIntent, newCustomer, userData }) {
  if (!orderId) { _devLog("skipped: missing orderId"); return false; }
  if (value == null || Number.isNaN(Number(value))) { _devLog("skipped: missing value"); return false; }

  const key = GOOGLE_ADS_FIRED_KEY(orderId);
  try {
    if (typeof window !== "undefined" && window.localStorage && localStorage.getItem(key)) {
      _devLog("dedupe blocked (already fired)", orderId);
      return true;
    }
  } catch { /* localStorage denied — proceed */ }

  // CRITICAL: `window.gtag` is created by GA4 the moment our analytics loader
  // runs, but the Google Ads DESTINATION (AW-341295704) isn't registered
  // until `installAdvertising` finishes. Firing a conversion into gtag
  // before AW is registered emits no network request. We therefore gate
  // strictly on our own `INSTALLED.ads` flag rather than on `typeof gtag`.
  if (!isAdsInstalled()) {
    _devLog("ads destination not installed — will retry");
    return false;
  }
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    _devLog("gtag unavailable — will retry");
    return false;
  }

  const c = (currency || "AED").toUpperCase();
  const numericValue = Number(value) || 0;
  const transactionId = paymentIntent || orderId;
  const isNewCustomer = newCustomer === true || newCustomer === "true";

  try {
    // Enhanced Conversions: gtag('set', 'user_data', ...) MUST be called
    // BEFORE the conversion event so the AW SDK attaches it. gtag hashes
    // SHA-256 client-side. Empty payload → skip (no PII harvested).
    //
    // Consent Mode v2 gate: the user_data payload may ONLY be sent when
    // `ad_user_data === 'granted'`. If the user rejected advertising
    // (or the CMP hasn't yet granted it), we still fire the conversion
    // hit, but strictly without any user identifiers — Google will
    // treat it as an unattributed cookieless conversion, which is the
    // correct GDPR behaviour.
    if (!isAdUserDataGranted()) {
      _devLog("enhanced conversion user_data suppressed (ad_user_data denied)");
    } else {
      const ud = _buildUserData(userData || {});
      if (Object.keys(ud).length > 0) {
        window.gtag("set", "user_data", ud);
        // Intentionally NO console log of ud — it contains PII.
        _devLog("enhanced conversion user_data set", { fields: Object.keys(ud) });
      }
    }
    window.gtag("event", "conversion", {
      send_to: GOOGLE_ADS_SEND_TO,
      value: numericValue,
      currency: c,
      transaction_id: transactionId,
      new_customer: isNewCustomer,
    });
    _devLog("conversion fired", { send_to: GOOGLE_ADS_SEND_TO, value: numericValue, currency: c, transaction_id: transactionId, new_customer: isNewCustomer });
  } catch (e) {
    _devLog("gtag conversion threw", e && e.message);
    return false;
  }

  // Only persist the dedupe key AFTER the gtag call has executed without
  // throwing AND after we verified the AW destination was installed.
  // Failed attempts never poison the store.
  try {
    localStorage.setItem(key, JSON.stringify({
      fired_at: new Date().toISOString(),
      transaction_id: transactionId,
      value: numericValue,
      currency: c,
    }));
  } catch { /* noop */ }
  return true;
}

/** Fire the non-Google-Ads pixels (GA4 purchase, Meta, TikTok, Snap) at
 * most once per order. Guarded by a separate dedupe key so a Google-Ads
 * failure/retry never re-fires these downstream pixels. */
function fireOtherPurchasePixels({ value, currency, orderId, trackCount, productIds, paymentIntent, newCustomer }) {
  if (!orderId) return;
  const key = OTHER_PIXELS_FIRED_KEY(orderId);
  try {
    if (typeof window !== "undefined" && window.localStorage && localStorage.getItem(key)) return;
  } catch { /* localStorage denied — proceed */ }

  const c = (currency || "AED").toUpperCase();
  const numericValue = Number(value) || 0;
  const items = (productIds || []).map((id) => ({ item_id: id }));
  const transactionId = paymentIntent || orderId;
  const isNewCustomer = newCustomer === true || newCustomer === "true";

  let anyFired = false;
  try {
    if (typeof window !== "undefined") {
      if (typeof window.gtag === "function") {
        // GA4 purchase (analytics — distinct from the Google Ads conversion).
        window.gtag("event", "purchase", {
          transaction_id: transactionId,
          value: numericValue,
          currency: c,
          new_customer: isNewCustomer,
          items,
        });
        anyFired = true;
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "Purchase", { value: numericValue, currency: c, content_ids: productIds || [], num_items: trackCount });
        anyFired = true;
      }
      if (window.ttq && typeof window.ttq.track === "function") {
        window.ttq.track("CompletePayment", { value: numericValue, currency: c, content_ids: productIds || [], quantity: trackCount });
        anyFired = true;
      }
      if (typeof window.snaptr === "function") {
        window.snaptr("track", "PURCHASE", { price: numericValue, currency: c, transaction_id: transactionId });
        anyFired = true;
      }
    }
  } catch { /* noop */ }

  // Only mark the "other pixels" dedupe key when at least one non-Google-Ads
  // pixel actually fired. Otherwise leave the flag off so a subsequent call
  // (once fbq / ttq / snaptr / gtag load) can pick them up.
  if (anyFired) {
    try { localStorage.setItem(key, "1"); } catch { /* noop */ }
  }
}

/** Fire a purchase conversion event exactly once per order.
 *
 * Return value is scoped to the **Google Ads** conversion outcome
 * (as required by Checkout.jsx's retry loop):
 *   - `true`  → the Google Ads conversion executed (now or previously).
 *   - `false` → the Google Ads conversion did NOT fire yet; caller should retry.
 *
 * The other pixels (GA4 purchase, Meta, TikTok, Snap) are fired
 * opportunistically and deduped under their own `djspy_pixels_fired_*` key.
 *
 * `userData` (optional) is used by Google Ads Enhanced Conversions.
 * Fields: `email, phone, firstName, lastName, country`. Missing fields are
 * silently dropped. gtag SHA-256 hashes them client-side before dispatch —
 * the raw values are NEVER logged to the console.
 */
export function firePurchase({
  value,
  currency,
  orderId,
  trackCount,
  productIds,
  newCustomer,
  paymentIntent,
  userData,
} = {}) {
  const googleAdsOk = fireGoogleAdsConversion({ value, currency, orderId, paymentIntent, newCustomer, userData });
  fireOtherPurchasePixels({ value, currency, orderId, trackCount, productIds, paymentIntent, newCustomer });
  return googleAdsOk;
}


// ---------------------------------------------------------------------------
// Snapchat helpers — VIEW_CONTENT + START_CHECKOUT (Fix 12 · Part B).
//
// Design notes:
//   * Consent gating is inherited from the existing pipeline — the
//     Snapchat base pixel is only installed when `advertising` consent
//     is granted (`installNonGoogleAds` above), so `window.snaptr` is
//     `undefined` when consent is denied and these calls become no-ops
//     without any additional flag plumbing.
//   * We NEVER call `snaptr` before the base pixel has installed, and
//     the guard `typeof window.snaptr === "function"` is checked in
//     every fire path.
//   * PURCHASE remains wired inside `fireOtherPurchasePixels` above so
//     it shares the existing `OTHER_PIXELS_FIRED_KEY(orderId)`
//     localStorage dedupe with GA4/Meta/TikTok.  A separate
//     sessionStorage guard is layered here as an extra safety net for
//     refresh/back-forward navigation on the same tab.
// ---------------------------------------------------------------------------

function _snapReady() {
  return typeof window !== "undefined" && typeof window.snaptr === "function";
}

/** Fire Snapchat VIEW_CONTENT for an eligible track/product detail
 * view.  Called once per meaningful detail-page view.  Silently
 * no-ops when consent has not enabled the Snapchat pixel (base
 * pixel isn't installed) or when a track_id is missing. */
export function fireSnapViewContent({ trackId, trackType, value, currency } = {}) {
  if (!_snapReady() || !trackId) return false;
  try {
    const params = { item_ids: [String(trackId)] };
    if (trackType) params.item_category = String(trackType);
    if (value != null && !Number.isNaN(Number(value))) params.price = Number(value);
    params.currency = (currency || "AED").toUpperCase();
    window.snaptr("track", "VIEW_CONTENT", params);
    return true;
  } catch { return false; }
}

/** Fire Snapchat START_CHECKOUT after Stripe successfully returns a
 * checkout session URL (see CatalogPrimitives.jsx / CartDrawer.jsx /
 * BundleCard.jsx).  Callers MUST invoke this only AFTER the
 * `/api/checkout/session` (or /cart / /bundle) response is confirmed
 * — never on the button click itself. */
export function fireSnapStartCheckout({ value, currency, productIds, numItems } = {}) {
  if (!_snapReady()) return false;
  try {
    const params = {};
    if (value != null && !Number.isNaN(Number(value))) params.price = Number(value);
    params.currency = (currency || "AED").toUpperCase();
    if (Array.isArray(productIds) && productIds.length) {
      params.item_ids = productIds.map(String);
    }
    if (numItems != null) params.number_items = Number(numItems);
    window.snaptr("track", "START_CHECKOUT", params);
    return true;
  } catch { return false; }
}
